import React, { useEffect, useState, useCallback } from 'react';
import { Table, Button, Select, Tag, Space, Statistic, Row, Col, Card, Modal, message } from 'antd';
import {
  PlusOutlined, EyeOutlined, SendOutlined, DeleteOutlined,
  FileTextOutlined, DollarOutlined, PercentageOutlined, DownloadOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { quotationApi, getApiBaseURL } from '../../services/api';
import dayjs from 'dayjs';

const QUOTATION_TYPES = ['产品销售', '项目工程', '运维服务'];
const QUOTATION_STATUSES = ['草稿', '已发送', '客户确认', '已成交', '已失效'];

const STATUS_COLORS = {
  '草稿': 'default',
  '已发送': 'blue',
  '客户确认': 'orange',
  '已成交': 'green',
  '已失效': 'red',
};

export default function Quotations() {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState({ page: 1, pageSize: 10, status: '', customerId: '', type: '' });
  const [stats, setStats] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: query.page, pageSize: query.pageSize };
      if (query.status) params.status = query.status;
      if (query.customerId) params.customerId = query.customerId;
      if (query.type) params.type = query.type;
      const res = await quotationApi.list(params);
      setData(res.data);
      setTotal(res.total);
    } finally {
      setLoading(false);
    }
  }, [query]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await quotationApi.stats({ period: 'month' });
      setStats(res);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchStats(); }, []);

  const handleSend = async (id) => {
    try {
      await quotationApi.send(id);
      message.success('报价已发送');
      fetchData();
      fetchStats();
    } catch (e) {
      message.error(e?.error || '操作失败');
    }
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除该报价吗？',
      okText: '确认删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        try {
          await quotationApi.delete(id);
          message.success('已删除');
          fetchData();
          fetchStats();
        } catch (e) {
          message.error(e?.error || '操作失败');
        }
      },
    });
  };

  const columns = [
    {
      title: '报价编号', dataIndex: 'code', key: 'code', width: 140,
      render: (code) => <a onClick={() => navigate(`/quotations/${data.find(d => d.code === code)?.id}`)}>{code}</a>,
    },
    {
      title: '客户', key: 'customer', width: 150,
      render: (_, r) => r.customer?.name || '—',
    },
    {
      title: '类型', dataIndex: 'type', key: 'type', width: 110,
      render: (v) => <Tag>{v}</Tag>,
    },
    {
      title: '报价总额', dataIndex: 'totalAmount', key: 'totalAmount', width: 150,
      render: (v) => `${v.toLocaleString('zh-CN', { style: 'currency', currency: 'CNY' })}`,
    },
    {
      title: '状态', dataIndex: 'status', key: 'status', width: 100,
      render: (v) => <Tag color={STATUS_COLORS[v] || 'default'}>{v}</Tag>,
    },
    {
      title: '日期', dataIndex: 'createdAt', key: 'createdAt', width: 120,
      render: (v) => dayjs(v).format('YYYY-MM-DD'),
    },
    {
      title: '操作', key: 'actions', width: 200,
      render: (_, r) => (
        <Space size="small">
          <Button size="small" icon={<EyeOutlined />} onClick={() => navigate(`/quotations/${r.id}`)}>
            查看
          </Button>
          {r.status === '草稿' && (
            <>
              <Button size="small" type="primary" icon={<SendOutlined />}
                onClick={() => handleSend(r.id)}>发送</Button>
              <Button size="small" danger icon={<DeleteOutlined />}
                onClick={() => handleDelete(r.id)}>删除</Button>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 20 }}>
        <Col span={8}>
          <Card size="small">
            <Statistic title="本月报价数" value={stats.periodCount || 0}
              prefix={<FileTextOutlined />} />
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small">
            <Statistic
              title="报价总额"
              value={stats.periodAmount || 0}
              precision={2}
              prefix={<DollarOutlined />}
              suffix="CNY"
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small">
            <Statistic
              title="转化率"
              value={stats.conversionRate || 0}
              suffix="%"
              prefix={<PercentageOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* 工具栏 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Space>
          <Select
            placeholder="筛选状态" allowClear style={{ width: 140 }}
            value={query.status || undefined}
            onChange={(v) => setQuery((q) => ({ ...q, status: v || '', page: 1 }))}
          >
            {QUOTATION_STATUSES.map((s) => (
              <Select.Option key={s} value={s}>{s}</Select.Option>
            ))}
          </Select>
          <Select
            placeholder="报价类型" allowClear style={{ width: 140 }}
            value={query.type || undefined}
            onChange={(v) => setQuery((q) => ({ ...q, type: v || '', page: 1 }))}
          >
            {QUOTATION_TYPES.map((t) => (
              <Select.Option key={t} value={t}>{t}</Select.Option>
            ))}
          </Select>
        </Space>
        <Space>
          <Button icon={<DownloadOutlined />} onClick={() => window.open(getApiBaseURL() + '/exports/quotations', '_blank')}>导出 CSV</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/quotations/new')}
            style={{ background: '#07c160', borderColor: '#07c160' }}>
            新建报价
          </Button>
        </Space>
      </div>

      {/* 表格 */}
      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={{
          total,
          current: query.page,
          pageSize: query.pageSize,
          showTotal: (t) => `共 ${t} 条报价`,
          onChange: (page) => setQuery((q) => ({ ...q, page })),
        }}
      />
    </div>
  );
}
